import { Component, Input, OnChanges } from '@angular/core';

import { ChartPointModel }           from '../typings/intelligence.chart-point';
import { ChartSeriesModel }          from '../typings/intelligence.chart-series';
import { ChartTooltipModel }         from '../typings/intelligence.chart-tooltip';
import { IntelligenceFormatService } from '../intelligence.format.service';

interface RenderedBar {
  color : string;
  height: number;
  width : number;
  x     : number;
  y     : number;
}

@Component({
  selector   : 'wor-intelligence-bar-chart',
  template: require('./intelligence.bar-chart.component.html')
})
export class BarChartComponent implements OnChanges {
  @Input() format = 'money';
  @Input() points : Array<ChartPointModel> = [];
  @Input() series : Array<ChartSeriesModel> = [];
  @Input() stacked = false;
  @Input() width = 640;

  bars : Array<RenderedBar> = [];
  height = 260;
  hoverIndex = -1;
  padding = { bottom: 26, left: 54, right: 14, top: 12 };
  ticks : Array<{ label : string; y : number; }> = [];
  tooltip : ChartTooltipModel = { rows: [], title: '', visible: false, x: 0, y: 0 };
  xLabels : Array<{ text : string; x : number; }> = [];

  private yMax = 1;

  constructor (
    private intelligenceFormatService: IntelligenceFormatService
  ) {}

  ngOnChanges () : void {
    this.render();
  }

  formatValue ( value : number ) : string {
    return this.format === 'count'
      ? this.intelligenceFormatService.count(value)
      : this.intelligenceFormatService.money(value);
  }

  hideTooltip () : void {
    this.hoverIndex = -1;
    this.tooltip = { ...this.tooltip, visible: false };
  }

  onMove ( event : MouseEvent ) : void {
    if ( !this.points.length ) {
      return;
    }

    const svg = event.currentTarget as SVGSVGElement;
    const bounds = svg.getBoundingClientRect();
    const plotX = ( event.clientX - bounds.left ) * ( this.width / bounds.width );
    const innerWidth = this.width - this.padding.left - this.padding.right;
    const step = innerWidth / this.points.length;

    this.hoverIndex = Math.min(
      Math.max(Math.floor(( plotX - this.padding.left ) / step), 0),
      this.points.length - 1
    );

    const point = this.points[ this.hoverIndex ];

    this.tooltip = {
      rows: this.series.map(item => ({
        color: item.color,
        label: item.label,
        value: this.formatValue(point.values[ item.key ] || 0)
      })),
      title  : point.label,
      visible: true,
      x      : Math.min(event.clientX - bounds.left + 14, bounds.width - 170),
      y      : Math.max(event.clientY - bounds.top - 10, 4)
    };
  }

  private niceCeil ( value : number ) : number {
    if ( value <= 0 ) {
      return 1;
    }

    const power = Math.pow(10, Math.floor(Math.log(value) / Math.LN10));
    const scaled = value / power;
    const nice = scaled <= 1
      ? 1
      : scaled <= 2
        ? 2
        : scaled <= 2.5
          ? 2.5
          : scaled <= 5
            ? 5
            : 10;

    return nice * power;
  }

  private pointY ( value : number ) : number {
    const innerHeight = this.height - this.padding.top - this.padding.bottom;

    return this.padding.top + innerHeight - ( Math.max(value, 0) / this.yMax ) * innerHeight;
  }

  private render () : void {
    this.bars = [];
    this.hideTooltip();
    this.ticks = [];
    this.xLabels = [];

    if ( !this.points.length || !this.series.length ) {
      return;
    }

    let max = 0;

    this.points.forEach(point => {
      if ( this.stacked ) {
        max = Math.max(max, this.series.reduce(( sum, item ) => sum + ( point.values[ item.key ] || 0 ), 0));
      } else {
        this.series.forEach(item => max = Math.max(max, point.values[ item.key ] || 0));
      }
    });

    this.yMax = this.niceCeil(max);

    const innerWidth = this.width - this.padding.left - this.padding.right;
    const step = innerWidth / this.points.length;
    const groupWidth = Math.min(step * 0.7, 44);
    const baseY = this.pointY(0);

    this.points.forEach(( point, index ) => {
      const groupLeft = this.padding.left + index * step + ( step - groupWidth ) / 2;

      if ( this.stacked ) {
        let running = 0;

        this.series.forEach(item => {
          const value = point.values[ item.key ] || 0;
          const top = this.pointY(running + value);
          const bottom = this.pointY(running);

          this.bars.push({
            color : item.color,
            height: Math.max(bottom - top, 0),
            width : groupWidth,
            x     : groupLeft,
            y     : top
          });

          running += value;
        });
      } else {
        const barWidth = groupWidth / this.series.length;

        this.series.forEach(( item, seriesIndex ) => {
          const value = point.values[ item.key ] || 0;
          const top = this.pointY(value);

          this.bars.push({
            color : item.color,
            height: Math.max(baseY - top, 0),
            width : Math.max(barWidth - 2, 2),
            x     : groupLeft + seriesIndex * barWidth,
            y     : top
          });
        });
      }
    });

    for ( let index = 0; index <= 4; index++ ) {
      const value = ( this.yMax / 4 ) * index;

      this.ticks.push({
        label: this.format === 'count'
          ? this.intelligenceFormatService.count(value)
          : this.intelligenceFormatService.compactMoney(value),
        y: this.pointY(value)
      });
    }

    const labelIndexes = this.points.length < 3
      ? this.points.map(( _point, index ) => index)
      : [ 0, Math.floor(( this.points.length - 1 ) / 2), this.points.length - 1 ];

    labelIndexes.forEach(index => {
      this.xLabels.push({
        text: this.points[ index ].label,
        x   : this.padding.left + index * step + step / 2
      });
    });
  }
}
